import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip
} from '@mui/material';
import {
  People,
  Visibility,
  TrendingUp,
  Public
} from '@mui/icons-material';

// Mock data - in a real app, this would come from an API
const mockVisitorData = {
  onlineUsers: 42,
  todayVisits: 567,
  weeklyGrowth: 12.5,
  topCountries: [
    { country: 'Kenya', visitors: 320 },
    { country: 'Tanzania', visitors: 85 },
    { country: 'Uganda', visitors: 64 },
    { country: 'Ethiopia', visitors: 42 }
  ]
};

interface VisitorMetricsProps {
  variant?: 'compact' | 'full';
  showTitle?: boolean;
}

const VisitorMetrics: React.FC<VisitorMetricsProps> = ({ 
  variant = 'full',
  showTitle = true 
}) => {
  const [visitorData, setVisitorData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Simulate API call to fetch visitor metrics
    const fetchVisitorMetrics = async () => {
      try {
        // In a real app, this would be an API call
        // const response = await fetch('/api/visitor-metrics');
        // const data = await response.json();
        
        // Using mock data for now
        setTimeout(() => {
          setVisitorData(mockVisitorData);
          setLoading(false);
        }, 800);
      } catch (error) {
        console.error('Error fetching visitor metrics:', error);
        setLoading(false);
      }
    };

    fetchVisitorMetrics();

    // Set up a refresh interval (every 30 seconds)
    const intervalId = setInterval(() => {
      fetchVisitorMetrics();
    }, 30000);

    return () => clearInterval(intervalId);
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!visitorData) {
    return (
      <Box sx={{ textAlign: 'center', p: 1 }}>
        <Typography variant="body2" color="text.secondary">
          No visitor data available
        </Typography>
      </Box>
    );
  }

  // Compact version just shows the online users chip
  if (variant === 'compact') {
    return (
      <Tooltip title="Current online users">
        <Chip 
          icon={<People fontSize="small" />} 
          label={`${visitorData.onlineUsers} online`}
          color="primary"
          size="small"
        />
      </Tooltip>
    );
  }

  // Full version shows all metrics
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      {showTitle && (
        <Typography variant="h6" gutterBottom>
          Visitor Metrics
        </Typography>
      )}
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Chip 
          icon={<People />} 
          label={`${visitorData.onlineUsers} online now`}
          color="primary"
          variant="outlined"
        />
        <Chip 
          icon={<Visibility />} 
          label={`${visitorData.todayVisits} today`}
          color="secondary"
          variant="outlined"
        />
        <Chip 
          icon={<TrendingUp />} 
          label={`${visitorData.weeklyGrowth}% this week`}
          color="success"
          variant="outlined"
        />
      </Box>
      
      <Divider sx={{ my: 1 }} />
      
      <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
        Top Visitor Locations
      </Typography>
      
      <List dense>
        {visitorData.topCountries.map((item: any, index: number) => (
          <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Public fontSize="small" color="action" />
            </ListItemIcon>
            <ListItemText 
              primary={item.country}
              secondary={`${item.visitors} visitors`}
              primaryTypographyProps={{ variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};

export default VisitorMetrics;